import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { differenceInDays, format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertCircle, AlertTriangle, Armchair, ArrowRight, ArrowUpCircle, BarChart3,
  Bed, Building2, Calendar as CalendarIcon, Car, Check, CheckCircle, Clock,
  Copy, Crown, Edit, Euro, Facebook, Gift, Heart, History, Hotel, Info,
  Loader2, Lock, LogIn, Mail, MapPin, Megaphone, MessageCircle, Percent,
  Phone, Plane, Plus, Save, Search, Share2, Shield, Sparkles, Star, Tag,
  Ticket, TrendingDown, TrendingUp, Twitter, User, UserCircle, Users,
  Utensils, UtensilsCrossed, Wallet, Zap,
} from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

// Already-extracted shared components
import { Breadcrumbs } from '../components/Breadcrumbs';
import { AuthDialog } from '../components/AuthDialog';

// Already-extracted dashboard tabs/dialogs (Fase 2)
import { TCKimlikUserCard } from './dashboard/TCKimlikUserCard';
import { B2BCreateOrderTab } from './dashboard/B2BCreateOrderTab';
import { B2BOrdersTab } from './dashboard/B2BOrdersTab';
import { BookingDetailsDialog } from './dashboard/BookingDetailsDialog';
import { GroupPassCodesTab } from './dashboard/GroupPassCodesTab';

// Circular import (named, hook-only) — same pattern as Fase 3b (AuthDialog).
// useAuth is only called inside DashboardPage render body, never at module load.
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * DashboardPage — Authenticated user dashboard with tabs for bookings,
 * favorites, flights, transfers, comparisons, travel credits, B2B orders,
 * group pass codes, and profile. Reads `?tab=` from URL for direct deep-links.
 *
 * Extracted from App.js (Fase 3c) — pure relocation, no logic changes.
 */
export const DashboardPage = () => {
  const { t } = useTranslation();
  const { user, token, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'bookings';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Update active tab when URL params change
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const { data: bookingsData, isLoading } = useQuery({
    queryKey: ['bookings'],
    queryFn: async () => {
      const response = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('freestays_token')}` },
        withCredentials: true
      });
      return response.data;
    },
    enabled: !!user
  });

  const { data: favoritesData, isLoading: favoritesLoading, refetch: refetchFavorites } = useQuery({
    queryKey: ['favorites'],
    queryFn: async () => {
      const response = await axios.get(`${API}/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!user && !!token
  });

  const { data: comparisonsData, isLoading: comparisonsLoading } = useQuery({
    queryKey: ['user-comparisons'],
    queryFn: async () => {
      const response = await axios.get(`${API}/user/price-comparisons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!user && !!token
  });

  // Travel Credits query
  const { data: creditsData, isLoading: creditsLoading } = useQuery({
    queryKey: ['travel-credits'],
    queryFn: async () => {
      const response = await axios.get(`${API}/user/travel-credits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!user && !!token
  });

  // Flight Bookings query
  const { data: flightBookingsData, isLoading: flightsLoading, refetch: refetchFlights } = useQuery({
    queryKey: ['flight-bookings'],
    queryFn: async () => {
      const response = await axios.get(`${API}/flights/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!user && !!token
  });

  const [resendingEmail, setResendingEmail] = useState(null);

  // Profile Data state
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    birth_date: '',
    gender: '',
    street: '',
    postal_code: '',
    city: '',
    country: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  // Load profile data
  useEffect(() => {
    if (user && token && !profileLoaded) {
      axios.get(`${API}/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setProfileData({
          first_name: res.data.first_name || '',
          last_name: res.data.last_name || '',
          phone: res.data.phone || '',
          birth_date: res.data.birth_date || '',
          gender: res.data.gender || '',
          street: res.data.street || '',
          postal_code: res.data.postal_code || '',
          city: res.data.city || '',
          country: res.data.country || ''
        });
        setProfileLoaded(true);
      }).catch(err => console.error('Failed to load profile:', err));
    }
  }, [user, token, profileLoaded]);

  // Save profile function
  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/user/profile`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('dashboard.profileSaved', 'Profile saved successfully!'));
    } catch (error) {
      toast.error(t('dashboard.profileSaveError', 'Failed to save profile'));
    } finally {
      setSavingProfile(false);
    }
  };

  const openBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setDetailsOpen(true);
  };

  const removeFavorite = async (hotelId) => {
    try {
      await axios.delete(`${API}/favorites/${hotelId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Removed from favorites");
      refetchFavorites();
    } catch (error) {
      toast.error("Failed to remove from favorites");
    }
  };

  const copyPassCode = () => {
    navigator.clipboard.writeText(user?.pass_code || '');
    toast.success("Pass code copied!");
  };

  // Handle buying extra pass directly (without requiring login redirect)
  const handleBuyExtraPass = (passType) => {
    navigate(`/booking/new?passPurchase=${passType}`);
  };

  const handleResendFlightEmail = async (bookingId) => {
    setResendingEmail(bookingId);
    try {
      const response = await axios.post(`${API}/flights/resend-email/${bookingId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.ok) {
        toast.success(t('dashboard.emailResent', 'Confirmation email has been resent!'));
      } else {
        toast.error(response.data.error || t('dashboard.emailResendFailed', 'Failed to resend email'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('dashboard.emailResendFailed', 'Failed to resend email'));
    } finally {
      setResendingEmail(null);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-gradient-to-b from-secondary/30 to-background">
        <Card className="p-10 text-center max-w-md rounded-3xl shadow-xl border-0 animate-fadeInUp">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <User className="w-10 h-10 text-primary" />
          </div>
          <h2 className="font-bold text-2xl mb-3">Please Sign In</h2>
          <p className="text-muted-foreground mb-8">Sign in to view your bookings and manage your Freestays Pass</p>
          <Dialog>
            <DialogTrigger asChild>
              <Button className="rounded-full px-8 h-12 text-base" data-testid="signin-btn">
                <LogIn className="w-5 h-5 mr-2" />
                Sign In
              </Button>
            </DialogTrigger>
            <AuthDialog />
          </Dialog>
        </Card>
      </div>
    );
  }

  const hasActivePass = user.pass_type === 'one_time' || user.pass_type === 'annual' || user.pass_type === 'b2b';
  // Statuses that mean the booking is "dead" — must NOT appear under Upcoming.
  // pending_payment is allowed: user may still complete payment.
  const INACTIVE_STATUSES = ['cancelled', 'refunded', 'no_show', 'cancellation_requested'];
  const isActive = (b) => !INACTIVE_STATUSES.includes(b?.status);
  const upcomingBookings = bookingsData?.bookings?.filter(b => isActive(b) && new Date(b.check_in) >= new Date()) || [];
  const pastBookings = bookingsData?.bookings?.filter(b => new Date(b.check_in) < new Date() || !isActive(b)) || [];
  const totalSaved = bookingsData?.bookings?.filter(isActive).reduce((acc, b) => acc + (b.discount_amount || 0), 0) || 0;

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gray-50 dark:bg-[#081D33] overflow-x-hidden" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Breadcrumbs */}
        <Breadcrumbs items={[
          { label: t('dashboard.title', 'My Dashboard') }
        ]} />
        
        {/* Hero Profile Section */}
        <div className="relative rounded-3xl bg-gradient-to-r from-primary to-primary/80 p-8 md:p-10 mb-8 overflow-hidden shadow-xl animate-fadeInUp">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
            <Avatar className="w-24 h-24 border-4 border-white/30 shadow-xl">
              <AvatarImage src={user.picture} />
              <AvatarFallback className="bg-white text-primary text-3xl font-semibold">
                {user.name?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className="font-bold text-2xl md:text-3xl text-primary-foreground">
                  {t('dashboard.welcome', { name: user.name?.split(' ')[0] })}
                </h1>
                {hasActivePass && (
                  <Badge className="bg-accent text-accent-foreground font-semibold animate-pulse-glow">
                    <Crown className="w-3.5 h-3.5 mr-1" />
                    {user.pass_type === 'annual' ? t('pass.annualPass', 'Annual Pass') : user.pass_type === 'b2b' ? 'B2B Pass' : t('pass.oneTimePass', 'One-Time Pass')}
                  </Badge>
                )}
              </div>
              <p className="text-primary-foreground/80">{user.email}</p>
            </div>

            {/* Quick Stats */}
            <div className="flex gap-4 md:gap-6">
              <div className="text-center px-4 py-2 bg-white/10 rounded-xl backdrop-blur-sm">
                <p className="text-2xl md:text-3xl font-bold text-[#00A9E0]-foreground">{bookingsData?.bookings?.length || 0}</p>
                <p className="text-xs text-primary-foreground/70">{t('dashboard.myBookings')}</p>
              </div>
              {totalSaved > 0 && (
                <div className="text-center px-4 py-2 bg-accent/30 rounded-xl backdrop-blur-sm">
                  <p className="text-2xl md:text-3xl font-bold text-accent">€{totalSaved.toFixed(0)}</p>
                  <p className="text-xs text-primary-foreground/70">{t('dashboard.totalSaved')}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="animate-fadeInUp stagger-1">
          <TabsList className="mb-8 bg-secondary/50 p-1 rounded-full overflow-x-auto flex-nowrap scrollbar-hide max-w-full">
            <TabsTrigger value="bookings" className="rounded-full px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-bookings">
              <Ticket className="w-4 h-4 mr-2" />
              {t('dashboard.myBookings')}
            </TabsTrigger>
            <TabsTrigger value="flights" className="rounded-full px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-flights">
              <Plane className="w-4 h-4 mr-2" />
              {t('dashboard.myFlights', 'My Flights')}
            </TabsTrigger>
            <TabsTrigger value="favorites" className="rounded-full px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-favorites">
              <Heart className="w-4 h-4 mr-2" />
              {t('dashboard.favorites')}
            </TabsTrigger>
            <TabsTrigger value="comparisons" className="rounded-full px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-comparisons">
              <BarChart3 className="w-4 h-4 mr-2" />
              {t('dashboard.myComparisons')}
            </TabsTrigger>
            <TabsTrigger value="passcode" className="rounded-full px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-passcode">
              <Crown className="w-4 h-4 mr-2" />
              {t('dashboard.myPass')}
            </TabsTrigger>
            <TabsTrigger value="credits" className="rounded-full px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-credits">
              <Wallet className="w-4 h-4 mr-2" />
              {t('dashboard.travelCredits', 'Travel Credits')}
            </TabsTrigger>
            <TabsTrigger value="profile" className="rounded-full px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-profile">
              <UserCircle className="w-4 h-4 mr-2" />
              {t('dashboard.personalData', 'Personal Data')}
            </TabsTrigger>
            {(user?.role === 'manager' || user?.group === 'managers' || user?.role === 'admin' || user?.is_admin || user?.group === 'b2b' || user?.pass_type === 'b2b') && (
              <TabsTrigger value="b2b" className="rounded-full px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-b2b">
                <Building2 className="w-4 h-4 mr-2" />
                B2B Orders
              </TabsTrigger>
            )}
            {(user?.group && user.group !== 'users' && user.group !== 'user') && (
              <TabsTrigger value="group-codes" className="rounded-full px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-group-codes">
                <Ticket className="w-4 h-4 mr-2" />
                Group Codes
              </TabsTrigger>
            )}
            {(user?.role === 'manager' || user?.group === 'managers' || user?.role === 'admin' || user?.is_admin || user?.role === 'account_manager') && (
              <TabsTrigger value="create-order" className="rounded-full px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-create-order">
                <Plus className="w-4 h-4 mr-2" />
                Create Order
              </TabsTrigger>
            )}
            {(user?.role === 'manager' || user?.group === 'managers' || user?.role === 'admin' || user?.is_admin || user?.role === 'account_manager' || user?.group === 'b2b') && (
              <button
                type="button"
                onClick={() => navigate('/movement')}
                className="rounded-full px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap inline-flex items-center justify-center font-medium text-muted-foreground hover:text-foreground hover:bg-background/50 transition"
                data-testid="tab-movement"
              >
                <Megaphone className="w-4 h-4 mr-2" />
                Movement
              </button>
            )}
          </TabsList>

          <TabsContent value="bookings">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2].map(i => (
                  <Card key={i} className="p-6 rounded-2xl">
                    <div className="flex gap-4">
                      <div className="w-32 h-24 skeleton rounded-xl" />
                      <div className="flex-1 space-y-3">
                        <div className="h-6 w-48 skeleton rounded" />
                        <div className="h-4 w-32 skeleton rounded" />
                        <div className="h-4 w-64 skeleton rounded" />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : bookingsData?.bookings?.length > 0 ? (
              <div className="space-y-8">
                {/* Trip Statistics Header */}
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary/10 via-accent/10 to-primary/5 dark:from-primary/20 dark:via-accent/15 dark:to-primary/10 p-8 border border-primary/10">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                  <div className="relative z-10">
                    <h2 className="font-sans font-bold text-2xl md:text-3xl mb-2">Your Travel Journey</h2>
                    <p className="text-muted-foreground mb-6">Every stay is a story waiting to be told</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white/60 dark:bg-card/60 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/50 dark:border-border/50">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Plane className="w-6 h-6 text-primary" />
                        </div>
                        <p className="text-2xl font-bold text-[#00A9E0]">{bookingsData.bookings.filter(isActive).length}</p>
                        <p className="text-xs text-muted-foreground">Total Trips</p>
                      </div>
                      <div className="bg-white/60 dark:bg-card/60 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/50 dark:border-border/50">
                        <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-2">
                          <CalendarIcon className="w-6 h-6 text-accent-foreground" />
                        </div>
                        <p className="text-2xl font-bold text-accent-foreground">{upcomingBookings.length}</p>
                        <p className="text-xs text-muted-foreground">Upcoming</p>
                      </div>
                      <div className="bg-white/60 dark:bg-card/60 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/50 dark:border-border/50">
                        <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Euro className="w-6 h-6 text-green-600 dark:text-green-400" />
                        </div>
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                          €{bookingsData.bookings.filter(isActive).reduce((acc, b) => acc + (b.discount_amount || 0), 0).toFixed(0)}
                        </p>
                        <p className="text-xs text-muted-foreground">Total Saved</p>
                      </div>
                      <div className="bg-white/60 dark:bg-card/60 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/50 dark:border-border/50">
                        <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Clock className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                        </div>
                        <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                          {bookingsData.bookings.filter(isActive).reduce((acc, b) => acc + differenceInDays(new Date(b.check_out), new Date(b.check_in)), 0)}
                        </p>
                        <p className="text-xs text-muted-foreground">Nights Booked</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Upcoming Bookings - Premium Design */}
                {upcomingBookings.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="font-bold text-xl md:text-2xl flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center shadow-lg">
                          <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        Upcoming Adventures
                      </h2>
                      <Badge className="bg-primary/10 text-primary border-[#00A9E0]/20 px-4 py-1.5 text-sm">
                        {upcomingBookings.length} {upcomingBookings.length === 1 ? 'trip' : 'trips'} ahead
                      </Badge>
                    </div>
                    <div className="space-y-6">
                      {upcomingBookings.map((booking, idx) => {
                        const daysUntil = differenceInDays(new Date(booking.check_in), new Date());
                        const nights = differenceInDays(new Date(booking.check_out), new Date(booking.check_in));
                        return (
                        <Card 
                          key={booking.booking_id} 
                          className="group relative overflow-hidden rounded-3xl border-0 shadow-xl hover:shadow-2xl transition-all duration-500 animate-fadeInUp bg-gradient-to-br from-card to-card/80"
                          style={{ animationDelay: `${idx * 0.1}s` }}
                          data-testid={`booking-${booking.booking_id}`}
                        >
                          {/* Decorative Elements */}
                          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
                          <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-accent/10 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
                          
                          <div className="relative flex flex-col lg:flex-row">
                            {/* Image/Visual Section */}
                            <div className="lg:w-72 h-56 lg:h-auto relative flex-shrink-0 overflow-hidden">
                              <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/80 to-accent">
                                <div className="absolute inset-0 opacity-20" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}} />
                              </div>
                              <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6">
                                <div className="text-center">
                                  <div className={`inline-flex items-center gap-2 backdrop-blur-sm rounded-full px-4 py-1.5 mb-4 ${booking.status === 'pending_payment' ? 'bg-amber-500/30' : booking.status === 'confirmation_pending' ? 'bg-orange-500/30' : 'bg-white/20'}`}>
                                    <div className={`w-2 h-2 rounded-full animate-pulse ${booking.status === 'pending_payment' ? 'bg-amber-400' : booking.status === 'confirmation_pending' ? 'bg-orange-400' : 'bg-green-400'}`} />
                                    <span className="text-sm font-medium">
                                      {booking.status === 'pending_payment'
                                        ? t('dashboard.pendingPayment', 'Pending Payment')
                                        : booking.status === 'confirmation_pending'
                                          ? t('dashboard.confirmationPending', 'Confirmation Pending')
                                          : booking.status === 'completed'
                                            ? t('dashboard.completed', 'Completed')
                                            : t('dashboard.confirmed', 'Confirmed')}
                                    </span>
                                  </div>
                                  <div className="text-6xl font-bold mb-1">{daysUntil}</div>
                                  <div className="text-white/80 text-sm">{daysUntil === 1 ? 'day' : 'days'} to go!</div>
                                </div>
                              </div>
                              {/* Corner Badge */}
                              <div className="absolute top-4 left-4">
                                <div className="bg-white/90 backdrop-blur-sm rounded-xl px-3 py-1.5 shadow-lg">
                                  <span className="text-xs font-semibold text-primary">{nights} {nights === 1 ? 'Night' : 'Nights'}</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Content Section */}
                            <div className="flex-1 p-6 lg:p-8">
                              <div className="flex items-start justify-between mb-5">
                                <div>
                                  <h3 className="font-sans font-bold text-xl lg:text-2xl mb-2 group-hover:text-[#00A9E0] transition-colors">{booking.hotel_name}</h3>
                                  <div className="flex items-center gap-3 text-muted-foreground">
                                    <span className="flex items-center gap-1.5 text-sm">
                                      <Bed className="w-4 h-4" />
                                      {booking.room_type}
                                    </span>
                                    <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
                                    <span className="flex items-center gap-1.5 text-sm">
                                      <Users className="w-4 h-4" />
                                      {booking.adults} {booking.adults === 1 ? 'Adult' : 'Adults'}{booking.children > 0 ? `, ${booking.children} ${booking.children === 1 ? 'Child' : 'Children'}` : ''}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Date Timeline */}
                              <div className="relative bg-gradient-to-r from-secondary/80 via-secondary/50 to-secondary/80 dark:from-secondary/50 dark:via-secondary/30 dark:to-secondary/50 rounded-2xl p-5 mb-5">
                                <div className="flex items-center justify-between relative">
                                  <div className="flex-1 text-center">
                                    <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Check-in</p>
                                    <p className="text-lg font-bold">{format(new Date(booking.check_in), 'EEE, MMM d')}</p>
                                    <p className="text-xs text-muted-foreground">{format(new Date(booking.check_in), 'yyyy')}</p>
                                  </div>
                                  <div className="flex-shrink-0 px-4">
                                    <div className="w-20 h-0.5 bg-gradient-to-r from-[#00A9E0]/50 via-primary to-primary/50 relative">
                                      <Plane className="w-5 h-5 text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-secondary dark:bg-card rounded-full p-0.5" />
                                    </div>
                                  </div>
                                  <div className="flex-1 text-center">
                                    <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Check-out</p>
                                    <p className="text-lg font-bold">{format(new Date(booking.check_out), 'EEE, MMM d')}</p>
                                    <p className="text-xs text-muted-foreground">{format(new Date(booking.check_out), 'yyyy')}</p>
                                  </div>
                                </div>
                              </div>

                              {/* Price & Savings */}
                              <div className="flex items-center justify-between pt-5 border-t border-border/50">
                                <div className="flex items-center gap-4">
                                  <div>
                                    <p className="text-xs text-muted-foreground">{booking.status === 'pending_payment' ? t('dashboard.totalDue', 'Total Due') : 'Total Paid'}</p>
                                    <p className="text-2xl font-bold text-[#00A9E0]">€{booking.final_price?.toFixed(2)}</p>
                                  </div>
                                  {booking.discount_amount > 0 && (
                                    <div className="bg-green-100 dark:bg-green-900/30 rounded-xl px-3 py-2">
                                      <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                                        <Gift className="w-4 h-4" />
                                        <span className="font-semibold">Saved €{booking.discount_amount.toFixed(2)}</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  {/* Reference Info */}
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button size="sm" variant="ghost" className="rounded-full h-9 px-3">
                                        <Info className="w-4 h-4 mr-1" />
                                        Ref
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-64 p-4" align="end">
                                      <p className="text-sm font-semibold mb-3">Booking References</p>
                                      <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                          <span className="text-muted-foreground">Booking ID:</span>
                                          <span className="font-mono text-xs">{booking.booking_id?.slice(-8)}</span>
                                        </div>
                                        {booking.confirmation_number && (
                                          <div className="flex justify-between">
                                            <span className="text-muted-foreground">Confirmation:</span>
                                            <span className="font-mono text-xs">{booking.confirmation_number}</span>
                                          </div>
                                        )}
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                  
                                  {/* Share Button */}
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button size="sm" variant="outline" className="rounded-full h-9 px-4">
                                        <Share2 className="w-4 h-4 mr-1.5" />
                                        Share
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-56 p-2" align="end">
                                      <p className="text-xs text-muted-foreground mb-2 px-2">Share your upcoming trip</p>
                                      <div className="space-y-1">
                                        <button 
                                          onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`I'm going to ${booking.hotel_name} on ${format(new Date(booking.check_in), 'MMM d, yyyy')}! Booked with Freestays - Room was FREE! 🎉`)}`, '_blank')}
                                          className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                                        >
                                          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                                            <MessageCircle className="w-4 h-4 text-white" />
                                          </div>
                                          WhatsApp
                                        </button>
                                        <button 
                                          onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`I'm going to ${booking.hotel_name}! Booked with @Freestays - Room was FREE! 🎉`)}`, '_blank')}
                                          className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg hover:bg-sky-50 dark:hover:bg-sky-900/20 transition-colors"
                                        >
                                          <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center">
                                            <Twitter className="w-4 h-4 text-white" />
                                          </div>
                                          Twitter / X
                                        </button>
                                        <button 
                                          onClick={() => {
                                            navigator.clipboard.writeText(`I'm going to ${booking.hotel_name} on ${format(new Date(booking.check_in), 'MMM d, yyyy')}! Booked with Freestays - Room was FREE!`);
                                            toast.success("Copied to clipboard!");
                                          }}
                                          className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg hover:bg-secondary transition-colors"
                                        >
                                          <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                                            <Copy className="w-4 h-4 text-muted-foreground" />
                                          </div>
                                          Copy Text
                                        </button>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                  
                                  {/* Details Button */}
                                  <Button 
                                    size="sm" 
                                    className="rounded-full h-9 px-5 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-lg hover:shadow-xl transition-all"
                                    onClick={() => openBookingDetails(booking)}
                                    data-testid={`view-details-${booking.booking_id}`}
                                  >
                                    View Details
                                    <ArrowRight className="w-4 h-4 ml-1.5" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                      )})}
                    </div>
                  </div>
                )}

                {/* Past Bookings - Memory Lane Style */}
                {pastBookings.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="font-bold text-xl md:text-2xl flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-muted to-muted-foreground/20 rounded-xl flex items-center justify-center">
                          <Clock className="w-5 h-5 text-muted-foreground" />
                        </div>
                        Travel Memories
                      </h2>
                      <span className="text-sm text-muted-foreground">{pastBookings.length} past {pastBookings.length === 1 ? 'trip' : 'trips'}</span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      {pastBookings.map((booking, idx) => {
                        const nights = differenceInDays(new Date(booking.check_out), new Date(booking.check_in));
                        return (
                        <Card 
                          key={booking.booking_id} 
                          className="group relative overflow-hidden rounded-2xl border border-border/50 hover:border-primary/30 hover:shadow-lg transition-all duration-300 cursor-pointer animate-fadeInUp"
                          style={{ animationDelay: `${idx * 0.05}s` }}
                          data-testid={`booking-past-${booking.booking_id}`}
                          onClick={() => openBookingDetails(booking)}
                        >
                          <div className="absolute inset-0 bg-gradient-to-br from-secondary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                          <div className="relative p-5">
                            <div className="flex items-start gap-4">
                              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                                <Building2 className="w-7 h-7 text-primary/60" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-lg truncate group-hover:text-[#00A9E0] transition-colors">{booking.hotel_name}</h3>
                                <p className="text-sm text-muted-foreground mt-0.5">
                                  {format(new Date(booking.check_in), 'MMM d')} - {format(new Date(booking.check_out), 'MMM d, yyyy')}
                                </p>
                                <div className="flex items-center gap-2 mt-3">
                                  <Badge variant="secondary" className="text-xs">
                                    {nights} {nights === 1 ? 'night' : 'nights'}
                                  </Badge>
                                  {booking.status === 'pending_payment' && (
                                    <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs border-0">
                                      {t('dashboard.pendingPayment', 'Pending Payment')}
                                    </Badge>
                                  )}
                                  {booking.discount_amount > 0 && (
                                    <Badge className="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-xs border-0">
                                      Saved €{booking.discount_amount.toFixed(0)}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-lg">€{booking.final_price?.toFixed(0)}</p>
                                <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <ArrowRight className="w-5 h-5 text-primary" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                      )})}
                    </div>
                  </div>
                )}

                {/* Book Another Trip CTA */}
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-primary via-primary to-accent p-8 text-white">
                  <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z" fill="%23ffffff" fill-opacity="1" fill-rule="evenodd"/%3E%3C/svg%3E")'}} />
                  <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-2">Ready for your next adventure?</h3>
                      <p className="text-white/80">Discover amazing hotels with FREE rooms waiting for you</p>
                    </div>
                    <Button 
                      onClick={() => navigate('/')} 
                      className="bg-white text-primary hover:bg-white/90 rounded-full px-8 h-12 font-semibold shadow-lg hover:shadow-xl transition-all"
                    >
                      <Search className="w-5 h-5 mr-2" />
                      Find Your Next Stay
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              /* Empty State - Inspiring Design */
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#00A9E0]/5 via-accent/5 to-primary/10 p-12 text-center">
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute top-10 left-10 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />
                  <div className="absolute bottom-10 right-10 w-40 h-40 bg-accent/10 rounded-full blur-3xl" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#00A9E0]/5 rounded-full blur-3xl" />
                </div>
                <div className="relative z-10">
                  <div className="w-28 h-28 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl">
                    <Plane className="w-14 h-14 text-white" />
                  </div>
                  <h3 className="font-sans font-bold text-3xl mb-4">{t('dashboard.journeyBeginsHere', 'Your Journey Begins Here')}</h3>
                  <p className="text-muted-foreground text-lg mb-8 max-w-lg mx-auto">
                    {t('dashboard.emptyStateDesc', 'Imagine waking up in a beautiful hotel room that cost you nothing. With Freestays, your room is FREE — you only pay for meals.')}
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button 
                      onClick={() => navigate('/')} 
                      className="rounded-full px-8 h-14 text-lg bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-xl hover:shadow-2xl transition-all" 
                      data-testid="find-hotels-btn"
                    >
                      <Search className="w-5 h-5 mr-2" />
                      {t('dashboard.exploreHotels', 'Explore Hotels')}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => navigate('/about')} 
                      className="rounded-full px-8 h-14 text-lg border-2"
                    >
                      <Info className="w-5 h-5 mr-2" />
                      {t('dashboard.howItWorks', 'How It Works')}
                    </Button>
                  </div>
                  <div className="mt-10 flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>{t('dashboard.hotelsCount', '450,000+ Hotels')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>{t('dashboard.roomIsFree', 'Room is FREE')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>{t('dashboard.instantBooking', 'Instant Booking')}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* My Flights Tab */}
          <TabsContent value="flights">
            {flightsLoading ? (
              <div className="space-y-4">
                {[1, 2].map(i => (
                  <Card key={i} className="p-6 rounded-2xl">
                    <div className="flex gap-4">
                      <div className="w-16 h-16 skeleton rounded-xl" />
                      <div className="flex-1 space-y-3">
                        <div className="h-5 w-48 skeleton rounded" />
                        <div className="h-4 w-32 skeleton rounded" />
                        <div className="h-4 w-64 skeleton rounded" />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : flightBookingsData?.bookings?.length > 0 ? (
              <div className="space-y-6">
                {/* Flights Stats */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <Card className="p-4 rounded-2xl text-center border-0 shadow-md bg-gradient-to-br from-sky-50 to-sky-100/50 dark:from-sky-950/30 dark:to-sky-900/20">
                    <Plane className="w-6 h-6 text-sky-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-sky-700 dark:text-sky-400">{flightBookingsData.bookings.length}</p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.totalFlights', 'Total Flights')}</p>
                  </Card>
                  <Card className="p-4 rounded-2xl text-center border-0 shadow-md bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-950/30 dark:to-green-900/20">
                    <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                      {flightBookingsData.bookings.filter(b => b.status === 'confirmed').length}
                    </p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.confirmed', 'Confirmed')}</p>
                  </Card>
                  <Card className="p-4 rounded-2xl text-center border-0 shadow-md bg-gradient-to-br from-[#00A9E0]/5 to-primary/10 dark:from-primary/20 dark:to-primary/10">
                    <Euro className="w-6 h-6 text-primary mx-auto mb-2" />
                    <p className="text-2xl font-bold text-[#00A9E0]">
                      €{flightBookingsData.bookings.reduce((acc, b) => acc + (b.customer_price_eur || 0), 0).toFixed(0)}
                    </p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.totalSpent', 'Total Spent')}</p>
                  </Card>
                </div>

                {/* Upcoming Flights */}
                {(() => {
                  const upcoming = flightBookingsData.bookings.filter(b => b.status === 'confirmed' || b.status === 'pending_payment');
                  const past = flightBookingsData.bookings.filter(b => b.status !== 'confirmed' && b.status !== 'pending_payment');
                  return (
                    <>
                      {upcoming.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                            <Plane className="w-5 h-5 text-primary" />
                            {t('dashboard.upcomingFlights', 'Upcoming Flights')}
                            <Badge variant="secondary" className="ml-1">{upcoming.length}</Badge>
                          </h3>
                          <div className="space-y-3">
                            {upcoming.map((fb) => (
                              <Card key={fb.booking_id} className="p-5 rounded-2xl hover:shadow-lg transition-shadow" data-testid={`flight-booking-${fb.booking_id}`}>
                                <div className="flex flex-col md:flex-row md:items-center gap-4">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className="w-12 h-12 bg-sky-100 dark:bg-sky-900/30 rounded-xl flex items-center justify-center shrink-0">
                                      <Plane className="w-6 h-6 text-sky-600" />
                                    </div>
                                    <div className="min-w-0">
                                      <p className="font-semibold text-base truncate">{fb.route || 'Flight'}</p>
                                      <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground mt-1">
                                        <span>{fb.adults} {t('dashboard.adults', 'adult(s)')}{fb.children > 0 ? `, ${fb.children} ${t('dashboard.children', 'child(ren)')}` : ''}</span>
                                        <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
                                        <span>{fb.created_at ? new Date(fb.created_at).toLocaleDateString() : ''}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3 flex-wrap">
                                    {fb.kiwi_pnr && (
                                      <div className="bg-sky-50 dark:bg-sky-950/30 rounded-lg px-3 py-1.5 border border-sky-200 dark:border-sky-800">
                                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">PNR</p>
                                        <p className="font-mono font-bold text-sky-700 dark:text-sky-400 text-sm">{fb.kiwi_pnr}</p>
                                      </div>
                                    )}
                                    <Badge className={fb.status === 'confirmed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800'} data-testid={`flight-status-${fb.booking_id}`}>
                                      {fb.status === 'confirmed' ? t('dashboard.statusConfirmed', 'Confirmed') : fb.status === 'pending_payment' ? t('dashboard.statusPending', 'Pending') : fb.status}
                                    </Badge>
                                    <p className="text-lg font-bold text-primary" data-testid={`flight-price-${fb.booking_id}`}>
                                      €{(fb.customer_price_eur || 0).toFixed(2)}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <Button variant="outline" size="sm" className="rounded-full gap-1.5 text-xs"
                                      disabled={resendingEmail === fb.booking_id}
                                      onClick={() => handleResendFlightEmail(fb.booking_id)}
                                      data-testid={`resend-email-${fb.booking_id}`}>
                                      {resendingEmail === fb.booking_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                                      {t('dashboard.resendEmail', 'Resend email')}
                                    </Button>
                                  </div>
                                </div>
                                {fb.kiwi_pnr && (
                                  <div className="mt-3 pt-3 border-t border-border/50 flex items-center gap-2 text-xs text-muted-foreground">
                                    <Armchair className="w-3.5 h-3.5 text-sky-500" />
                                    {t('dashboard.seatSelectionTip', 'Select your seat on the airline\'s website using your PNR code')}
                                  </div>
                                )}
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}

                      {past.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-lg mb-3 flex items-center gap-2 text-muted-foreground">
                            <Clock className="w-5 h-5" />
                            {t('dashboard.pastFlights', 'Past Flights')}
                            <Badge variant="outline" className="ml-1">{past.length}</Badge>
                          </h3>
                          <div className="space-y-3">
                            {past.map((fb) => (
                              <Card key={fb.booking_id} className="p-4 rounded-2xl opacity-75 hover:opacity-100 transition-opacity" data-testid={`flight-booking-${fb.booking_id}`}>
                                <div className="flex flex-col md:flex-row md:items-center gap-3">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className="w-10 h-10 bg-muted rounded-xl flex items-center justify-center shrink-0">
                                      <Plane className="w-5 h-5 text-muted-foreground" />
                                    </div>
                                    <div className="min-w-0">
                                      <p className="font-medium text-sm truncate">{fb.route || 'Flight'}</p>
                                      <p className="text-xs text-muted-foreground">{fb.created_at ? new Date(fb.created_at).toLocaleDateString() : ''}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3 flex-wrap">
                                    {fb.kiwi_pnr && (
                                      <span className="font-mono text-xs text-muted-foreground">PNR: {fb.kiwi_pnr}</span>
                                    )}
                                    <Badge variant="outline" className="text-xs">
                                      {fb.status === 'cancelled' ? t('dashboard.statusCancelled', 'Cancelled') : fb.status === 'refunded' ? t('dashboard.statusRefunded', 'Refunded') : fb.status}
                                    </Badge>
                                    <p className="font-semibold text-sm">€{(fb.customer_price_eur || 0).toFixed(2)}</p>
                                  </div>
                                  <Button variant="ghost" size="sm" className="rounded-full gap-1.5 text-xs shrink-0"
                                    disabled={resendingEmail === fb.booking_id}
                                    onClick={() => handleResendFlightEmail(fb.booking_id)}
                                    data-testid={`resend-email-${fb.booking_id}`}>
                                    {resendingEmail === fb.booking_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                                    {t('dashboard.resendEmail', 'Resend email')}
                                  </Button>
                                </div>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            ) : (
              <Card className="p-12 text-center rounded-3xl border-0 shadow-lg">
                <div className="w-20 h-20 bg-sky-100 dark:bg-sky-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Plane className="w-10 h-10 text-sky-500" />
                </div>
                <h3 className="font-bold text-xl mb-2">{t('dashboard.noFlights', 'No flight bookings yet')}</h3>
                <p className="text-muted-foreground mb-6 text-sm">{t('dashboard.noFlightsDesc', 'Book your first flight and see your bookings here')}</p>
                <Button onClick={() => navigate('/flights')} className="rounded-full px-8" data-testid="search-flights-btn">
                  <Plane className="w-4 h-4 mr-2" />
                  {t('dashboard.searchFlights', 'Search Flights')}
                </Button>
              </Card>
            )}
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            {favoritesLoading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <Card key={i} className="rounded-2xl overflow-hidden">
                    <div className="h-48 skeleton" />
                    <div className="p-5 space-y-3">
                      <div className="h-5 w-3/4 skeleton rounded" />
                      <div className="h-4 w-1/2 skeleton rounded" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : favoritesData?.favorites?.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favoritesData.favorites.map((fav, idx) => (
                  <Card 
                    key={fav.hotel_id} 
                    className="rounded-2xl overflow-hidden shadow-lg border-0 group card-hover animate-fadeInUp"
                    style={{ animationDelay: `${idx * 0.1}s` }}
                    data-testid={`favorite-${fav.hotel_id}`}
                  >
                    <div className="relative h-48 overflow-hidden bg-secondary">
                      {fav.image_url ? (
                        <img 
                          src={fav.image_url} 
                          alt={fav.hotel_name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Hotel className="w-10 h-10 text-muted-foreground/30" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                      
                      {/* Remove button */}
                      <button
                        onClick={() => removeFavorite(fav.hotel_id)}
                        className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white transition-all shadow-lg"
                        data-testid={`remove-fav-${fav.hotel_id}`}
                      >
                        <Heart className="w-5 h-5 fill-current" />
                      </button>

                      {/* Star Rating */}
                      {fav.star_rating && (
                        <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-0.5">
                          {Array.from({ length: Math.floor(fav.star_rating) }).map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                      )}

                      {/* Price Badge */}
                      {fav.min_price && (
                        <div className="absolute bottom-3 right-3 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-semibold">
                          From €{fav.min_price?.toFixed(0)}
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="font-semibold text-lg line-clamp-1 mb-1">{fav.hotel_name}</h3>
                      <p className="text-sm text-muted-foreground flex items-center gap-1 mb-4">
                        <MapPin className="w-3.5 h-3.5" />
                        {fav.location}
                      </p>
                      <Button 
                        className="w-full rounded-full"
                        onClick={() => navigate(`/hotel/${fav.hotel_id}?adults=2&children=0&destination=${encodeURIComponent(fav.location || '')}&destinationId=${fav.dest_id || ''}`)}
                      >
                        View Hotel
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-16 text-center rounded-3xl border-0 shadow-lg bg-gradient-to-br from-secondary/50 to-background">
                <div className="w-24 h-24 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="w-12 h-12 text-red-300" />
                </div>
                <h3 className="font-bold text-2xl mb-3">{t('dashboard.noFavorites', 'No favorites yet')}</h3>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                  {t('dashboard.clickHeart', 'Click the heart icon on any hotel to save it to your favorites!')}
                </p>
                <Button onClick={() => navigate('/search')} className="rounded-full px-8 h-12">
                  <Search className="w-5 h-5 mr-2" />
                  {t('dashboard.exploreHotels', 'Explore Hotels')}
                </Button>
              </Card>
            )}
          </TabsContent>

          {/* My Price Comparisons Tab */}
          <TabsContent value="comparisons">
            {comparisonsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <Card key={i} className="p-6 rounded-2xl">
                    <div className="flex gap-4">
                      <div className="w-16 h-16 skeleton rounded-xl" />
                      <div className="flex-1 space-y-3">
                        <div className="h-6 w-48 skeleton rounded" />
                        <div className="h-4 w-32 skeleton rounded" />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : comparisonsData?.comparisons?.length > 0 ? (
              <div className="space-y-4">
                {comparisonsData.comparisons.map((comparison, idx) => (
                  <Card 
                    key={comparison.comparison_id} 
                    className="p-0 rounded-2xl overflow-hidden shadow-lg border-0 card-hover animate-fadeInUp cursor-pointer"
                    style={{ animationDelay: `${idx * 0.1}s` }}
                    onClick={() => {
                      // Navigate to search with these parameters
                      navigate(`/search?destination=${encodeURIComponent(comparison.destination)}&destinationId=${comparison.destination_id}&checkIn=${comparison.check_in}&checkOut=${comparison.check_out}&adults=${comparison.adults || 2}&children=${comparison.children || 0}`);
                    }}
                    data-testid={`comparison-${comparison.comparison_id}`}
                  >
                    <div className="flex flex-col md:flex-row">
                      {/* Icon Section */}
                      <div className="md:w-24 h-24 md:h-auto bg-gradient-to-br from-primary/20 to-accent/10 flex items-center justify-center flex-shrink-0">
                        <BarChart3 className="w-10 h-10 text-primary/50" />
                      </div>
                      
                      {/* Content Section */}
                      <div className="flex-1 p-6">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-bold text-lg mb-1 flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-primary" />
                              {comparison.destination}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {comparison.check_in} → {comparison.check_out} • {comparison.guests}
                            </p>
                          </div>
                          {comparison.total_savings > 0 && (
                            <Badge className="bg-accent/20 text-accent-foreground font-semibold">
                              Save €{comparison.total_savings?.toFixed(0)}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-muted-foreground">
                            {comparison.hotels_count} {t('search.results', 'hotels found').replace('{{count}}', '')}
                          </span>
                          <span className="text-primary font-medium">
                            {comparison.hotels_with_savings} {t('dashboard.hotelsSaved', '{{count}} hotels with savings').replace('{{count}}', '')}
                          </span>
                          <span className="text-xs text-muted-foreground ml-auto">
                            {t('dashboard.searchedOn')}: {new Date(comparison.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      
                      {/* Arrow */}
                      <div className="hidden md:flex items-center px-4">
                        <ArrowRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-16 text-center rounded-3xl border-0 shadow-lg bg-gradient-to-br from-secondary/50 to-background">
                <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <BarChart3 className="w-12 h-12 text-primary/30" />
                </div>
                <h3 className="font-bold text-2xl mb-3">{t('dashboard.noComparisons')}</h3>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                  {t('dashboard.noComparisonsDesc')}
                </p>
                <Button onClick={() => navigate('/')} className="rounded-full px-8 h-12">
                  <Search className="w-5 h-5 mr-2" />
                  {t('dashboard.searchHotels')}
                </Button>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="passcode">
            <div className="grid md:grid-cols-2 gap-8 min-w-0">
              {/* Pass Card */}
              <Card className={`p-8 rounded-3xl border-0 shadow-xl overflow-hidden relative min-w-0 ${hasActivePass ? 'bg-gradient-to-br from-primary to-primary/90' : 'bg-card'}`}>
                {hasActivePass && (
                  <>
                    <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-accent/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
                  </>
                )}
                
                <div className="relative">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${hasActivePass ? 'bg-white/20' : 'bg-secondary'}`}>
                    {hasActivePass ? (
                      <Crown className="w-8 h-8 text-primary-foreground" />
                    ) : (
                      <Tag className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>
                  
                  <h2 className={`font-bold text-2xl mb-2 ${hasActivePass ? 'text-primary-foreground' : ''}`}>
                    {hasActivePass ? 'Your Freestays Pass' : 'Get Freestays Pass'}
                  </h2>
                  <p className={`mb-6 ${hasActivePass ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                    {hasActivePass 
                      ? t('dashboard.passActiveDesc', 'Your room with meal package is FREE on every booking!')
                      : 'Unlock free rooms and exclusive savings'}
                  </p>

                  {hasActivePass && (
                    <>
                      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6 min-w-0">
                        <p className="text-sm text-primary-foreground/70 mb-2">Your Pass Code</p>
                        <p className="font-mono text-2xl sm:text-3xl font-bold tracking-wider text-primary-foreground mb-4 break-all" data-testid="user-passcode">
                          {user.pass_code}
                        </p>
                        <Button 
                          variant="secondary" 
                          onClick={copyPassCode} 
                          className="rounded-full bg-white/20 hover:bg-white/30 text-primary-foreground border-0"
                          data-testid="copy-passcode"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Code
                        </Button>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-primary-foreground/70">Pass Type</span>
                        <Badge className="bg-accent text-accent-foreground">
                          {user.pass_type === 'annual' ? 'Annual Pass' : user.pass_type === 'b2b' ? 'B2B Pass' : 'One-Time Pass'}
                        </Badge>
                      </div>
                      {(user.pass_type === 'annual' || user.pass_type === 'b2b') && user.pass_expires_at && (
                        <div className="flex items-center justify-between text-sm mt-3">
                          <span className="text-primary-foreground/70">Valid Until</span>
                          <span className="text-primary-foreground font-medium">
                            {format(new Date(user.pass_expires_at), 'MMM d, yyyy')}
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  {/* Buy Extra Pass Section - for users with active pass */}
                  {hasActivePass && (
                    <div className="mt-6 pt-6 border-t border-white/20">
                      <p className="text-sm text-primary-foreground/70 mb-3">Want to buy a pass for someone else?</p>
                      <div className="space-y-3">
                        <Card 
                          className="p-4 border-2 border-white/30 hover:border-white cursor-pointer transition-all rounded-xl group bg-white/10"
                          onClick={() => handleBuyExtraPass('one_time')}
                          data-testid="buy-extra-onetime"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-primary-foreground group-hover:text-white transition-colors">One-Time Pass</p>
                              <p className="text-xs text-primary-foreground/60">FREE room on next booking</p>
                            </div>
                            <span className="text-xl font-bold text-primary-foreground">€35</span>
                          </div>
                        </Card>
                        <Card 
                          className="p-4 border-2 border-accent hover:border-accent/80 cursor-pointer transition-all rounded-xl group bg-accent/20"
                          onClick={() => handleBuyExtraPass('annual')}
                          data-testid="buy-extra-annual"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-accent-foreground group-hover:text-white transition-colors">Annual Pass</p>
                              <p className="text-xs text-primary-foreground/60">Unlimited FREE rooms for 1 year</p>
                            </div>
                            <span className="text-xl font-bold text-accent-foreground">€129</span>
                          </div>
                        </Card>
                      </div>
                    </div>
                  )}

                  {!hasActivePass && (
                    <div className="space-y-4">
                      <Card 
                        className="p-5 border-2 hover:border-primary cursor-pointer transition-all rounded-2xl group"
                        onClick={() => handleBuyExtraPass('one_time')}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold group-hover:text-[#00A9E0] transition-colors">One-Time Pass</p>
                            <p className="text-sm text-muted-foreground">FREE room on your next booking</p>
                          </div>
                          <span className="text-2xl font-bold text-[#00A9E0]">€35</span>
                        </div>
                      </Card>
                      <Card 
                        className="p-5 border-2 border-primary bg-[#00A9E0]/5 cursor-pointer transition-all rounded-2xl group"
                        onClick={() => handleBuyExtraPass('annual')}
                      >
                        <div className="absolute top-2 right-2">
                          <Badge className="bg-accent text-accent-foreground text-xs">BEST VALUE</Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-primary">Annual Pass</p>
                            <p className="text-sm text-muted-foreground">Unlimited FREE rooms for 1 year</p>
                          </div>
                          <span className="text-2xl font-bold text-[#00A9E0]">€129</span>
                        </div>
                      </Card>
                    </div>
                  )}
                </div>
              </Card>

              {/* Referral Card - Phase 6: Enhanced Marketing Tools */}
              <Card className="p-8 rounded-3xl border-0 shadow-xl bg-gradient-to-br from-[#00A9E0]/5 to-accent/5 min-w-0 overflow-hidden">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center">
                    <Gift className="w-6 h-6 text-accent-foreground" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl">{t('referral.title', 'Refer Friends & Earn')}</h3>
                    <p className="text-sm text-muted-foreground">{t('referral.subtitle', 'Share your code and help friends save.')}</p>
                    <p className="text-sm text-muted-foreground">{t('referral.earnCredit', 'Earn credit for future bookings')}</p>
                  </div>
                </div>
                
                <div className="space-y-4 min-w-0">
                  <div className="p-4 bg-white rounded-2xl border shadow-sm min-w-0">
                    <p className="text-xs text-muted-foreground mb-1">{t('referral.yourCode', 'Your Referral Code')}</p>
                    <div className="flex items-center justify-between gap-2 flex-wrap min-w-0">
                      <span className="text-lg sm:text-2xl font-mono font-bold text-primary tracking-wider break-all min-w-0">{user?.referral_code || 'Loading...'}</span>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(user?.referral_code || '');
                          toast.success(t('referral.codeCopied', 'Referral code copied!'));
                        }}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        {t('common.copy', 'Copy')}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3 min-w-0">
                    <div className="p-4 bg-white rounded-xl text-center min-w-0">
                      <p className="text-2xl font-bold text-[#00A9E0]">{user?.referral_count || 0}</p>
                      <p className="text-xs text-muted-foreground">{t('referral.friendsReferred', 'Friends Referred')}</p>
                    </div>
                    <div className="p-4 bg-white rounded-xl text-center min-w-0">
                      <p className="text-2xl font-bold text-[#00A9E0]">€{(user?.travel_credits || 0).toFixed(0)}</p>
                      <p className="text-xs text-muted-foreground">{t('referral.creditsEarned', 'Credits Earned')}</p>
                    </div>
                    <div className="p-4 bg-white rounded-xl text-center min-w-0">
                      <p className="text-lg font-bold text-accent">{t('referral.upTo50', 'up to 50%')}</p>
                      <p className="text-xs text-muted-foreground">{t('referral.cashBack', 'Cash Back')}</p>
                    </div>
                  </div>
                  
                  {/* Share Buttons - Phase 6: Enhanced sharing options */}
                  <div className="space-y-2 min-w-0">
                    <p className="text-sm font-medium text-muted-foreground">{t('referral.shareVia', 'Share via:')}</p>
                    <div className="grid grid-cols-2 gap-2 min-w-0">
                      <Button 
                        className="rounded-full bg-green-500 hover:bg-green-600"
                        onClick={() => {
                          const text = `${t('referral.shareMessage', 'Join FreeStays and get FREE hotel rooms! Use my code:')} ${user?.referral_code}. ${t('referral.shareMessageSuffix', 'Your room is FREE - you only pay for meals!')} 🏨✨ https://freestays.eu`;
                          window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                        }}
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        WhatsApp
                      </Button>
                      <Button 
                        variant="outline"
                        className="rounded-full border-blue-400 text-blue-600 hover:bg-blue-50"
                        onClick={() => {
                          const text = `${t('referral.shareMessage', 'Join FreeStays and get FREE hotel rooms! Use my code:')} ${user?.referral_code}`;
                          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=https://freestays.eu`, '_blank');
                        }}
                      >
                        <Twitter className="w-4 h-4 mr-2" />
                        Twitter
                      </Button>
                      <Button 
                        variant="outline"
                        className="rounded-full border-blue-600 text-blue-700 hover:bg-blue-50"
                        onClick={() => {
                          window.open(`https://www.facebook.com/sharer/sharer.php?u=https://freestays.eu&quote=${encodeURIComponent(`Join FreeStays with my code: ${user?.referral_code}`)}`, '_blank');
                        }}
                      >
                        <Facebook className="w-4 h-4 mr-2" />
                        Facebook
                      </Button>
                      <Button 
                        variant="outline"
                        className="rounded-full"
                        onClick={() => {
                          const text = `${t('referral.shareMessage', 'Join FreeStays and get FREE hotel rooms! Use my code:')} ${user?.referral_code}\n\nhttps://freestays.eu`;
                          navigator.clipboard.writeText(text);
                          toast.success(t('referral.messageCopied', 'Share message copied!'));
                        }}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        {t('referral.copyLink', 'Copy Link')}
                      </Button>
                    </div>
                  </div>
                  
                  {/* How it works section */}
                  <div className="p-4 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border border-[#00A9E0]/20">
                    <p className="font-semibold text-sm mb-2">{t('referral.howItWorks', 'How it works:')}</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li className="flex items-start gap-2">
                        <Check className="w-3 h-3 text-[#00A9E0] mt-0.5 flex-shrink-0" />
                        {t('referral.step1', 'Share your code with friends')}
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-3 h-3 text-[#00A9E0] mt-0.5 flex-shrink-0" />
                        {t('referral.step2', 'They sign up and buy a FreeStays Pass')}
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-3 h-3 text-[#00A9E0] mt-0.5 flex-shrink-0" />
                        {t('referral.step3', 'Earn up to 50% cash back on your pass!')}
                      </li>
                    </ul>
                  </div>
                </div>
              </Card>

              {/* FreeStays Pass Benefits Widget - Phase 3 */}
              <Card className="p-6 rounded-3xl border-0 shadow-xl bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border border-emerald-200 dark:border-emerald-800 md:col-span-2">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
                    <Utensils className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl">{t('hero.freeWithMeals', 'Free Room with Meals')}</h3>
                    <p className="text-sm text-muted-foreground">{t('dashboard.passEligibleDesc', 'Hotels where your FreeStays Pass unlocks free rooms')}</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  {/* Network Stats */}
                  <div className="bg-white/60 dark:bg-card/60 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/50 dark:border-border/50">
                    <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Hotel className="w-5 h-5 text-[#00A9E0] dark:text-[#00A9E0]" />
                    </div>
                    <p className="text-2xl font-bold text-[#00A9E0] dark:text-[#00A9E0]">400,000+</p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.eligibleHotels', 'Eligible Hotels')}</p>
                  </div>
                  <div className="bg-white/60 dark:bg-card/60 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/50 dark:border-border/50">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Car className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">50,000+</p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.withTransfers', 'With Transfers')}</p>
                  </div>
                  <div className="bg-white/60 dark:bg-card/60 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/50 dark:border-border/50">
                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/50 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Percent className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">30-50%</p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.avgSavings', 'Avg. Savings')}</p>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex flex-wrap gap-3">
                  <Button 
                    className="rounded-full bg-[#00A9E0] hover:bg-emerald-600 text-white"
                    onClick={() => navigate('/search?destination=Amsterdam&mealPackage=true')}
                  >
                    <Utensils className="w-4 h-4 mr-2" />
                    {t('dashboard.findMealPackages', 'Find Meal Package Hotels')}
                  </Button>
                  <Button 
                    variant="outline"
                    className="rounded-full border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-950"
                    onClick={() => navigate('/search?destination=Amsterdam&transfers=true')}
                  >
                    <Car className="w-4 h-4 mr-2" />
                    {t('dashboard.findTransferHotels', 'Find Hotels with Transfers')}
                  </Button>
                  <Button 
                    variant="outline"
                    className="rounded-full"
                    onClick={() => navigate('/last-minute')}
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    {t('dashboard.lastMinuteDeals', 'Last Minute Deals')}
                  </Button>
                </div>
              </Card>

              {/* Personal Data Summary Card */}
              <Card className="p-6 rounded-3xl border-0 shadow-xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                      <UserCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="font-bold text-lg">{t('dashboard.personalData', 'Personal Data')}</h3>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => setActiveTab('profile')}
                    className="text-primary"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    {t('common.edit', 'Edit')}
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{t('checkout.name', 'Name')}:</span>
                    <span className="font-medium">
                      {profileData.first_name || profileData.last_name 
                        ? `${profileData.first_name} ${profileData.last_name}`.trim() 
                        : <span className="text-muted-foreground italic">{t('dashboard.notSet', 'Not set')}</span>}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{t('checkout.email', 'Email')}:</span>
                    <span className="font-medium">{user?.email || '-'}</span>
                    {user?.email_verified ? (
                      <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                        <Check className="w-3 h-3 mr-1" />
                        {t('dashboard.verified', 'Verified')}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        {t('dashboard.notVerified', 'Not verified')}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{t('checkout.phone', 'Phone')}:</span>
                    <span className="font-medium">
                      {profileData.phone || <span className="text-muted-foreground italic">{t('dashboard.notSet', 'Not set')}</span>}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{t('dashboard.address', 'Address')}:</span>
                    <span className="font-medium">
                      {profileData.city || profileData.country 
                        ? `${profileData.city}${profileData.city && profileData.country ? ', ' : ''}${profileData.country}`.trim() 
                        : <span className="text-muted-foreground italic">{t('dashboard.notSet', 'Not set')}</span>}
                    </span>
                  </div>
                </div>
                
                {(!profileData.first_name || !profileData.phone) && (
                  <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-xl border border-amber-200 dark:border-amber-800">
                    <p className="text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {t('dashboard.completeProfile', 'Complete your profile for faster checkout!')}
                    </p>
                  </div>
                )}
              </Card>

              {/* Benefits Card */}
              <Card className="p-8 rounded-3xl border-0 shadow-xl">
                <h3 className="font-bold text-xl mb-6">Get your Freestays Benefits With the</h3>
                <p className="text-sm text-foreground/80 mb-6 -mt-4">€35 One-Time Pass or €129 Annual Pass</p>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-accent/10 rounded-2xl">
                    <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                      <Gift className="w-5 h-5 text-accent-foreground" />
                    </div>
                    <div>
                      <p className="font-semibold">Room = FREE</p>
                      <p className="text-sm text-muted-foreground">We give our commission back to you — that's your room price!</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-secondary/50 rounded-2xl">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Percent className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">No Booking Fee</p>
                      <p className="text-sm text-muted-foreground">No booking fees with Freestays</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-secondary/50 rounded-2xl">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">{t('dashboard.hotelsCount', '450,000+ Hotels')}</p>
                      <p className="text-sm text-muted-foreground">{t('dashboard.hotelsWorldwide', 'Access to hotels worldwide with no commission')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-secondary/50 rounded-2xl">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">{t('dashboard.bestPrice', 'Best Price Guarantee')}</p>
                      <p className="text-sm text-muted-foreground">{t('dashboard.bestPriceDesc', 'Hotels save 30% vs Booking.com — we pass it to you')}</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Travel Credits Tab */}
          <TabsContent value="credits">
            <div className="space-y-6">
              {/* Credits Balance Card */}
              <Card className="rounded-3xl border-0 shadow-xl overflow-hidden">
                <div className="bg-gradient-to-r from-primary to-blue-600 p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/80 text-sm">{t('dashboard.availableCredits', 'Available Travel Credits')}</p>
                      <p className="text-4xl font-bold mt-1">
                        €{creditsData?.available_credits?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                      <Wallet className="w-8 h-8" />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/20 flex justify-between text-sm">
                    <div>
                      <p className="text-white/60">{t('dashboard.totalEarned', 'Total Earned')}</p>
                      <p className="font-semibold">€{creditsData?.total_credits?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div>
                      <p className="text-white/60">{t('dashboard.used', 'Used')}</p>
                      <p className="font-semibold">€{creditsData?.credits_used?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div>
                      <p className="text-white/60">{t('dashboard.maxCredits', 'Max Credits')}</p>
                      <p className="font-semibold">€{creditsData?.max_credits?.toFixed(2) || '64.50'}</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                        creditsData?.tier === 'Diamond' ? 'bg-cyan-100' :
                        creditsData?.tier === 'Elite' ? 'bg-yellow-100' :
                        creditsData?.tier === 'Insider' ? 'bg-slate-100' :
                        creditsData?.tier === 'Explorer' ? 'bg-amber-100' : 'bg-gray-100'
                      }`}>
                        {creditsData?.tier === 'Diamond' ? '💎' :
                         creditsData?.tier === 'Elite' ? '🥇' :
                         creditsData?.tier === 'Insider' ? '🥈' :
                         creditsData?.tier === 'Explorer' ? '🥉' : '⭐'}
                      </div>
                      <div>
                        <p className="font-bold">{creditsData?.tier || 'Starter'} {t('dashboard.tier', 'Tier')}</p>
                        <p className="text-sm text-muted-foreground">{creditsData?.tier_percent || 5}% {t('dashboard.feedback', 'feedback per tier')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">{t('dashboard.referralCount', 'Referrals')}</p>
                      <p className="text-2xl font-bold text-[#00A9E0]">{creditsData?.referral_count || 0}</p>
                    </div>
                  </div>
                  
                  {!creditsData?.has_annual_pass && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4 mb-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800 dark:text-amber-200">{t('dashboard.annualPassRequired', 'Annual Pass Required')}</p>
                          <p className="text-sm text-amber-700 dark:text-amber-300">{t('dashboard.annualPassRequiredDesc', 'Upgrade to Annual Pass to earn travel credits from referrals.')}</p>
                          <Button size="sm" className="mt-2" onClick={() => navigate('/?upgrade=annual')}>
                            {t('dashboard.upgradeNow', 'Upgrade Now')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  <Separator className="my-4" />
                  
                  <div>
                    <h4 className="font-semibold mb-3">{t('dashboard.useCreditsFor', 'Use Your Credits For')}</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 p-3 bg-secondary/50 rounded-xl">
                        <UtensilsCrossed className="w-5 h-5 text-primary" />
                        <span className="text-sm">{t('dashboard.foodBeverage', 'Food & Beverage')}</span>
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-secondary/50 rounded-xl">
                        <ArrowUpCircle className="w-5 h-5 text-primary" />
                        <span className="text-sm">{t('dashboard.roomUpgrades', 'Room Upgrades')}</span>
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-secondary/50 rounded-xl">
                        <Ticket className="w-5 h-5 text-primary" />
                        <span className="text-sm">{t('dashboard.futureBookings', 'Future Bookings')}</span>
                      </div>
                      <div className="flex items-center gap-2 p-3 bg-secondary/50 rounded-xl">
                        <Sparkles className="w-5 h-5 text-primary" />
                        <span className="text-sm">{t('dashboard.specialServices', 'Special Services')}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Credits History */}
              <Card className="rounded-3xl border-0 shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5" />
                    {t('dashboard.creditsHistory', 'Credits History')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {creditsData?.history?.length > 0 ? (
                    <div className="space-y-3">
                      {creditsData.history.map((entry, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#0a2540] rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              entry.amount > 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                            }`}>
                              {entry.amount > 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                            </div>
                            <div>
                              <p className="font-medium">
                                {entry.source === 'referral' ? t('dashboard.referralCredit', 'Referral Credit') : t('dashboard.creditUsed', 'Credit Used')}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {entry.tier && `${entry.tier} • `}
                                {new Date(entry.timestamp).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <p className={`font-bold ${entry.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {entry.amount > 0 ? '+' : ''}€{Math.abs(entry.amount).toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Wallet className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>{t('dashboard.noCreditsYet', 'No credits yet')}</p>
                      <p className="text-sm">{t('dashboard.startReferring', 'Start referring friends to earn travel credits!')}</p>
                      <Button className="mt-4" onClick={() => navigate('/refer-a-friend')}>
                        {t('dashboard.referFriends', 'Refer Friends')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Personal Data Tab */}
          <TabsContent value="profile">
            <Card className="p-6 rounded-2xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <UserCircle className="w-6 h-6 text-primary" />
                  {t('dashboard.personalData', 'Personal Data')}
                </h3>
                {!user?.email_verified && (
                  <Badge variant="outline" className="text-orange-600 border-orange-300">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    {t('dashboard.emailNotVerified', 'Email not verified')}
                  </Badge>
                )}
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg border-b pb-2">{t('dashboard.personalInfo', 'Personal Information')}</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name">{t('checkout.firstName', 'First Name')}</Label>
                      <Input
                        id="first_name"
                        value={profileData.first_name}
                        onChange={(e) => setProfileData(prev => ({ ...prev, first_name: e.target.value }))}
                        placeholder={t('checkout.firstName', 'First Name')}
                        data-testid="profile-first-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name">{t('checkout.lastName', 'Last Name')}</Label>
                      <Input
                        id="last_name"
                        value={profileData.last_name}
                        onChange={(e) => setProfileData(prev => ({ ...prev, last_name: e.target.value }))}
                        placeholder={t('checkout.lastName', 'Last Name')}
                        data-testid="profile-last-name"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">{t('checkout.email', 'Email')}</Label>
                    <Input
                      id="email"
                      value={user?.email || ''}
                      disabled
                      className="bg-secondary/50"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">{t('checkout.phone', 'Phone Number')}</Label>
                    <Input
                      id="phone"
                      value={profileData.phone}
                      onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+31 6 12345678"
                      data-testid="profile-phone"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="birth_date">{t('dashboard.birthDate', 'Birth Date')}</Label>
                      <Input
                        id="birth_date"
                        type="date"
                        value={profileData.birth_date}
                        onChange={(e) => setProfileData(prev => ({ ...prev, birth_date: e.target.value }))}
                        data-testid="profile-birth-date"
                      />
                    </div>
                    <div>
                      <Label htmlFor="gender">{t('dashboard.gender', 'Gender')}</Label>
                      <Select value={profileData.gender} onValueChange={(val) => setProfileData(prev => ({ ...prev, gender: val }))}>
                        <SelectTrigger data-testid="profile-gender">
                          <SelectValue placeholder={t('dashboard.selectGender', 'Select gender')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">{t('dashboard.male', 'Male')}</SelectItem>
                          <SelectItem value="female">{t('dashboard.female', 'Female')}</SelectItem>
                          <SelectItem value="other">{t('dashboard.other', 'Other')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg border-b pb-2">{t('dashboard.addressInfo', 'Address Information')}</h4>
                  
                  <div>
                    <Label htmlFor="street">{t('dashboard.street', 'Street & Number')}</Label>
                    <Input
                      id="street"
                      value={profileData.street}
                      onChange={(e) => setProfileData(prev => ({ ...prev, street: e.target.value }))}
                      placeholder={t('dashboard.streetPlaceholder', 'Main Street 123')}
                      data-testid="profile-street"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="postal_code">{t('dashboard.postalCode', 'Postal Code')}</Label>
                      <Input
                        id="postal_code"
                        value={profileData.postal_code}
                        onChange={(e) => setProfileData(prev => ({ ...prev, postal_code: e.target.value }))}
                        placeholder="1234 AB"
                        data-testid="profile-postal-code"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">{t('dashboard.city', 'City')}</Label>
                      <Input
                        id="city"
                        value={profileData.city}
                        onChange={(e) => setProfileData(prev => ({ ...prev, city: e.target.value }))}
                        placeholder={t('dashboard.cityPlaceholder', 'Amsterdam')}
                        data-testid="profile-city"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="country">{t('dashboard.country', 'Country')}</Label>
                    <Input
                      id="country"
                      value={profileData.country}
                      onChange={(e) => setProfileData(prev => ({ ...prev, country: e.target.value }))}
                      placeholder={t('dashboard.countryPlaceholder', 'Netherlands')}
                      data-testid="profile-country"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Button onClick={saveProfile} disabled={savingProfile} data-testid="save-profile-btn">
                  {savingProfile ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('common.saving', 'Saving...')}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {t('dashboard.saveProfile', 'Save Profile')}
                    </>
                  )}
                </Button>
              </div>
            </Card>
            
            {/* TC Kimlik / Nationality Gate Card (only shown if user is Turkish or has a status) */}
            <TCKimlikUserCard user={user} token={token} onUpdate={(v) => setUser(prev => prev ? { ...prev, tc_kimlik_status: v } : prev)} />
            
            {/* Change Password Card */}
            <Card className="p-6 rounded-2xl mt-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Lock className="w-6 h-6 text-primary" />
                  {t('dashboard.changePassword', 'Change Password')}
                </h3>
              </div>
              
              <div className="max-w-md space-y-4">
                <div>
                  <Label htmlFor="current_password">{t('dashboard.currentPassword', 'Current Password')}</Label>
                  <Input
                    id="current_password"
                    type="password"
                    value={passwordData?.current || ''}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, current: e.target.value }))}
                    placeholder="••••••••"
                    data-testid="current-password"
                  />
                </div>
                
                <div>
                  <Label htmlFor="new_password">{t('dashboard.newPassword', 'New Password')}</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={passwordData?.new || ''}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, new: e.target.value }))}
                    placeholder="••••••••"
                    data-testid="new-password"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t('dashboard.passwordRequirements', 'Minimum 8 characters')}</p>
                </div>
                
                <div>
                  <Label htmlFor="confirm_password">{t('dashboard.confirmPassword', 'Confirm New Password')}</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={passwordData?.confirm || ''}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirm: e.target.value }))}
                    placeholder="••••••••"
                    data-testid="confirm-password"
                  />
                </div>
                
                <Button 
                  onClick={async () => {
                    if (!passwordData?.current || !passwordData?.new || !passwordData?.confirm) {
                      toast.error(t('dashboard.fillAllFields', 'Please fill in all password fields'));
                      return;
                    }
                    if (passwordData.new !== passwordData.confirm) {
                      toast.error(t('dashboard.passwordsDontMatch', 'New passwords do not match'));
                      return;
                    }
                    if (passwordData.new.length < 8) {
                      toast.error(t('dashboard.passwordTooShort', 'Password must be at least 8 characters'));
                      return;
                    }
                    
                    try {
                      setChangingPassword(true);
                      const response = await axios.post(`${API}/auth/change-password`, {
                        current_password: passwordData.current,
                        new_password: passwordData.new
                      }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      
                      if (response.data.success) {
                        toast.success(t('dashboard.passwordChanged', 'Password changed successfully'));
                        setPasswordData({ current: '', new: '', confirm: '' });
                      }
                    } catch (error) {
                      toast.error(error.response?.data?.detail || t('dashboard.passwordChangeFailed', 'Failed to change password'));
                    } finally {
                      setChangingPassword(false);
                    }
                  }}
                  disabled={changingPassword}
                  data-testid="change-password-btn"
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('common.saving', 'Saving...')}
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      {t('dashboard.updatePassword', 'Update Password')}
                    </>
                  )}
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* B2B Orders Tab - visible for B2B group + Managers + Admin */}
          {(user?.role === 'manager' || user?.group === 'managers' || user?.role === 'admin' || user?.is_admin || user?.group === 'b2b' || user?.pass_type === 'b2b') && (
            <TabsContent value="b2b">
              <B2BOrdersTab user={user} isManager={user?.role === 'manager' || user?.group === 'managers' || user?.role === 'admin' || user?.is_admin} />
            </TabsContent>
          )}

          {/* Group-assigned Pass Codes - visible for any user that belongs to a custom group */}
          {(user?.group && user.group !== 'users' && user.group !== 'user') && (
            <TabsContent value="group-codes">
              <GroupPassCodesTab user={user} setUser={setUser} />
            </TabsContent>
          )}

          {/* Create Order Tab - visible for Managers + Admin only */}
          {(user?.role === 'manager' || user?.group === 'managers' || user?.role === 'admin' || user?.is_admin || user?.role === 'account_manager') && (
            <TabsContent value="create-order">
              <B2BCreateOrderTab user={user} />
            </TabsContent>
          )}
        </Tabs>
      </div>
      
      {/* Booking Details Dialog */}
      <BookingDetailsDialog 
        booking={selectedBooking} 
        open={detailsOpen} 
        onOpenChange={setDetailsOpen} 
      />
    </div>
  );
};

export default DashboardPage;
